import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Divider,
  useTheme,
  TextField,
  IconButton,
  MenuItem,
} from "@mui/material";
import { PageFieldCard } from "../cards/PageFieldCard";
import { useAPI } from "../../hooks";
import { useToast } from "../../context";

const TEXT_VALIDATION_RULES = [
  {
    id: "location-name",
    name: "Location name",
    regex: "^[\\p{L}0-9-]{2,50}$",
    description: "Allows multi-language letters, numbers, and hyphens; length 2–50",
    regexType: 1,
  },
  {
    id: "user-name",
    name: "User Name",
    regex: "^[\\p{L}' ]{2,100}$",
    description: "Allows multi-language letters, spaces, and apostrophe '; length 2–100",
    regexType: 2,
  },
  {
    id: "phone-number",
    name: "Phone Number",
    regex: "^\\+[0-9]{1,15}$",
    description: "Must start with +; followed by 1–15 digits; no spaces or symbols allowed",
    regexType: 3,
  },
  {
    id: "email",
    name: "Email",
    regex: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]{2,}$",
    description: "Must follow standard email format. Must contain \"@\" and a valid domain. No spaces",
    regexType: 4,
  },
  {
    id: "amount-of-money",
    name: "Amount of money",
    regex: "^(?:0|[1-9][0-9]{0,5}|1000000)$",
    description: "Range: 0 to 1,000,000",
    regexType: 5,
  },
];

const FILE_VALIDATION_RULES = [
  {
    id: "images-only",
    name: "Image file name",
    regex: "^[\\p{L}0-9 _-]+\\.(jpg|jpeg|png|webp|bmp)$",
    description: "Allows image file formats (jpg, jpeg, png, webp, bmp) with valid filename characters",
    regexType: 6,
  },
  {
    id: "all-files",
    name: "all file name",
    regex: "^[\\p{L}0-9 _-]+\\.[A-Za-z0-9]{1,10}$",
    description: "Allows all file types with valid filename characters and extension (1-10 characters)",
    regexType: 7,
  },
];

export const AddPageFieldModal = (props) => {
  const { open, onClose, onSubmit, formID, field, isEdit } = props;
  const theme = useTheme();
  const [formField, setFormField] = useState({
    type: "text",
    label: "",
    validation: {
      required: false,
      min_length: 20,
      max_length: 55,
      regex: null,
    },
    status: true,
  });
  const [selectedRule, setSelectedRule] = useState("");
  const [minLength, setMinLength] = useState("");
  const [maxLength, setMaxLength] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [customRegex, setCustomRegex] = useState("");
  const [isEditInitialized, setIsEditInitialized] = useState(false);
  const { api, urls, isLoading } = useAPI();
  const { showToast } = useToast();

  useEffect(() => {
    if (!open) {
      // Reset when modal closes
      setSelectedRule("");
      setMinLength("");
      setMaxLength("");
      setIsEditMode(false);
      setCustomRegex("");
      setIsEditInitialized(false);
      return;
    }

    if (isEdit && field) {
      setFormField({
        type: field?.type ?? "text",
        label: field?.label ?? "",
        validation: {
          required: field?.validation?.required ?? false,
          min_length: field?.validation?.min_length ?? 20,
          max_length: field?.validation?.max_length ?? 55,
          regex: field?.validation?.regex ?? null,
        },
        status: field?.status ?? true,
      });
      
      // Use regexType from backend to determine selected rule
      if (field?.validation?.regex && field?.validation?.regex !== null) {
        const regexType = Number(field?.validation?.regexType);
        
        // Load min/max length from saved regex
        const savedRegex = field.validation.regex;
        const lengthMatch = savedRegex.match(/\{(\d+),?(\d*)\}/);
        if (lengthMatch) {
          setMinLength(lengthMatch[1] || "");
          setMaxLength(lengthMatch[2] || "");
        } else {
          setMinLength("");
          setMaxLength("");
        }
        
        // Set flag to prevent useEffect overwrites during initialization
        setIsEditInitialized(true);
        
        if (regexType === 0 || isNaN(regexType)) {
          // Custom regex
          setSelectedRule("custom");
          setCustomRegex(field.validation.regex);
          setIsEditMode(false);
        } else {
          // Find rule by regexType
          const allRules = [...TEXT_VALIDATION_RULES, ...FILE_VALIDATION_RULES];
          const matchedRule = allRules.find((rule) => rule.regexType === regexType);
          if (matchedRule) {
            setSelectedRule(matchedRule.id);
            setIsEditMode(false);
            setCustomRegex("");
          } else {
            // Fallback to custom if regexType not found
            setSelectedRule("custom");
            setCustomRegex(field.validation.regex);
            setIsEditMode(false);
          }
        }
      } else {
        setSelectedRule("");
        setIsEditMode(false);
        setCustomRegex("");
        setMinLength("");
        setMaxLength("");
      }
    } else if (!isEdit) {
      // Reset for add mode
      setFormField({
        type: "text",
        label: "",
        validation: {
          required: false,
          min_length: 20,
          max_length: 55,
          regex: null,
        },
        status: true,
      });
      setSelectedRule("");
      setMinLength("");
      setMaxLength("");
      setIsEditMode(false);
      setCustomRegex("");
    }
  }, [field, isEdit, open]);

  // Clear edit initialization flag after a tick to allow normal useEffect behavior
  useEffect(() => {
    if (isEditInitialized) {
      const timer = setTimeout(() => setIsEditInitialized(false), 100);
      return () => clearTimeout(timer);
    }
  }, [isEditInitialized]);

  const getCurrentValidationRules = () => {
    return formField.type === "file" ? FILE_VALIDATION_RULES : TEXT_VALIDATION_RULES;
  };

  const [userChangedType, setUserChangedType] = useState(false);
  
  useEffect(() => {
    // Only reset when user manually changes the type, not on initial load
    if (!userChangedType) return;
    
    setSelectedRule("");
    setMinLength("");
    setMaxLength("");
    setIsEditMode(false);
    setCustomRegex("");
    setUserChangedType(false);
  }, [formField.type, userChangedType]);


  const handleRuleChange = (event) => {
    const value = event.target.value;
    setSelectedRule(value);
    if (value === "custom") {
      setIsEditMode(true);
      setCustomRegex("");
      setMinLength("");
      setMaxLength("");
    } else if (value) {
      setIsEditMode(false);
      setCustomRegex("");
      // Extract default min/max length from selected rule's regex
      const currentRules = getCurrentValidationRules();
      const rule = currentRules.find((r) => r.id === value);
      if (rule) {
        const lengthMatch = rule.regex.match(/\{(\d+),?(\d*)\}/);
        if (lengthMatch) {
          setMinLength(lengthMatch[1] || "");
          setMaxLength(lengthMatch[2] || "");
        } else {
          setMinLength("");
          setMaxLength("");
        }
      }
    } else {
      // None selected
      setIsEditMode(false);
      setCustomRegex("");
      setMinLength("");
      setMaxLength("");
    }
  };

  const hasLengthConstraints = () => {
    if (!selectedRule) return false;
    // Don't show length override for file validations - length constraints are for extensions, not overridable
    if (formField.type === "file") return false;
    
    const currentRules = getCurrentValidationRules();
    const rule = currentRules.find((r) => r.id === selectedRule);
    if (!rule) return false;
    return /\{\d+/.test(rule.regex);
  };

  const getGeneratedRegex = () => {
    if (!selectedRule) {
      return "";
    }
    
    const currentRules = getCurrentValidationRules();
    const rule = currentRules.find((r) => r.id === selectedRule);
    if (!rule) return "";

    // If length overrides are provided, modify the regex
    if (hasLengthConstraints() && (minLength || maxLength)) {
      const min = minLength || "0";
      const max = maxLength || "";
      const lengthPattern = max ? `{${min},${max}}` : `{${min},}`;
      
      // Replace the length constraint in the regex
      return rule.regex.replace(/\{\d+,?\d*\}/, lengthPattern);
    }

    return rule.regex;
  };

  const getFinalRegex = () => {
    if (selectedRule === "custom") {
      return customRegex || "Enter custom regex pattern";
    }
    return getGeneratedRegex();
  };


  // Get regexType based on selectedRule
  const getRegexType = () => {
    if (!selectedRule || selectedRule === "") return null;
    if (selectedRule === "custom") return 0;
    
    const currentRules = getCurrentValidationRules();
    const rule = currentRules.find((r) => r.id === selectedRule);
    return rule ? rule.regexType : 0;
  };

  // Update formField.validation.regex whenever the final regex changes
  useEffect(() => {
    // Skip during edit initialization - we already have the saved regex
    if (isEditInitialized) return;
    
    let finalRegex = null;
    if (selectedRule === "custom") {
      finalRegex = customRegex && customRegex.trim() !== "" ? customRegex : null;
    } else if (selectedRule) {
      const currentRules = getCurrentValidationRules();
      const rule = currentRules.find((r) => r.id === selectedRule);
      if (rule) {
        if (hasLengthConstraints() && (minLength || maxLength)) {
          const min = minLength || "0";
          const max = maxLength || "";
          const lengthPattern = max ? `{${min},${max}}` : `{${min},}`;
          finalRegex = rule.regex.replace(/\{\d+,?\d*\}/, lengthPattern);
        } else {
          finalRegex = rule.regex;
        }
      }
    }
    
    setFormField((prev) => ({
      ...prev,
      validation: {
        ...prev.validation,
        regex: finalRegex,
      },
    }));
  }, [selectedRule, minLength, maxLength, customRegex, formField.type, isEditInitialized]);

  const isValidRegex = (regexString) => {
    if (!regexString || regexString.trim() === "") {
      return true; // Empty regex is valid (no validation)
    }
    try {
      // Test if regex can be compiled
      const regex = new RegExp(regexString);
      
      // Additional check: ensure it's not just a plain literal string
      // A valid regex pattern should have at least one of:
      // - Anchors (^ or $)
      // - Special characters (., *, +, ?, |, [], {}, etc.)
      // - Character classes ([...], \d, \w, \s, etc.)
      // - Escaped characters (\., \+, etc.)
      const hasPatternFeatures = /[\^$.*+?|\[\]{}()\\]/.test(regexString);
      
      if (!hasPatternFeatures && regexString.length < 3) {
        // Very short strings without pattern features might be intentional
        return true;
      }
      
      // If it's a longer string without pattern features, it's probably just a literal
      // which might not be what the user intended
      if (!hasPatternFeatures && regexString.length >= 3) {
        return false; // Likely a plain literal string, not a pattern
      }
      
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleSubmit = () => {
    let params = { ...formField };

    // Validate regex if it exists and is not null
    if (params.validation.regex !== null && params.validation.regex && params.validation.regex.trim() !== "") {
      if (!isValidRegex(params.validation.regex)) {
        showToast("Invalid regex pattern. Please use proper regex syntax with pattern matching features (e.g., ^, $, ., *, +, [], etc.).", "error");
        return;
      }
    }
    
    // Always send regex and regexType
    params.validation.regexType = getRegexType();

    if (!isEdit) {
      api
        .post(urls.addFormField + formID, params)
        .then((res) => {
          showToast("New filed added", "success");
          onSubmit();
        })
        .catch((err) => {
          showToast(err.response?.data?.data?.message, "error");
        });
    } else {
      api
        .put(urls.editFormField + field.id + "/", params)
        .then((res) => {
          showToast("Field updated", "success");
          onSubmit();
        })
        .catch((err) => {
          showToast(err.response?.data?.data?.message, "error");
        });
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose} sx={{ p: 2 }}>
      <DialogTitle textAlign={"center"} variant="h1">
        {isEdit ? "Edit Field" : "Add Field"}
      </DialogTitle>
      <DialogContent sx={{ mt: 2, mb: 2 }}>
        <Stack direction={"column"} sx={{ gap: 3 }}>
          <PageFieldCard
            field={formField}
            onFieldChange={(data) => {
              if (data.type !== formField.type) {
                setUserChangedType(true);
              }
              setFormField(data);
            }}
            isPopup={true}
            isEdit={isEdit}
          />

          <Divider />

          <Box
            sx={{
              pt: 2,
              pl: 3,
              pr: 3,
              pb: 3,
              borderRadius: 3,
              bgcolor: theme.palette.popupBlue.main,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                color: theme.palette.primary.main,
                fontWeight: 600,
              }}
            >
              Validation Rule
            </Typography>
            <TextField
              fullWidth
              select
              value={selectedRule}
              onChange={handleRuleChange}
              placeholder="Select a validation rule"
              inputProps={{
                sx: {
                  fontSize: 16,
                  fontWeight: 400,
                  color: theme.palette.primary.main,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "white",
                  "& fieldset": {
                    borderColor: theme.palette.secondary.main,
                  },
                },
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {getCurrentValidationRules().map((rule) => (
                <MenuItem key={rule.id} value={rule.id}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {rule.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: theme.palette.text.secondary,
                        mt: 0.5,
                      }}
                    >
                      {rule.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
              <MenuItem value="custom">
                <Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: theme.palette.primary.main,
                    }}
                  >
                    Custom
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: theme.palette.text.secondary,
                      mt: 0.5,
                    }}
                  >
                    Enter your own custom regex pattern
                  </Typography>
                </Box>
              </MenuItem>
            </TextField>
            {selectedRule === "custom" && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  value={customRegex}
                  onChange={(e) => setCustomRegex(e.target.value)}
                  placeholder="Enter custom regex pattern (e.g., ^[A-Za-z0-9]+$)"
                  inputProps={{
                    sx: {
                      fontFamily: "monospace",
                      fontSize: 14,
                      color: theme.palette.primary.main,
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "white",
                      "& fieldset": {
                        borderColor: theme.palette.secondary.main,
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Box>

          {selectedRule && selectedRule !== "custom" && hasLengthConstraints() && (
            <>
              <Divider />
              <Box
                sx={{
                  pt: 2,
                  pl: 3,
                  pr: 3,
                  pb: 3,
                  borderRadius: 3,
                  bgcolor: theme.palette.popupBlue.main,
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    mb: 2,
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  }}
                >
                  Override Length Constraints
                </Typography>
                <Stack direction={"row"} sx={{ gap: 2 }}>
                  <TextField
                    label="Min Length"
                    type="number"
                    value={minLength}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || (!isNaN(value) && parseInt(value) >= 0)) {
                        setMinLength(value);
                      }
                    }}
                    inputProps={{
                      min: 0,
                      sx: {
                        fontSize: 16,
                        fontWeight: 400,
                        color: theme.palette.primary.main,
                      },
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "white",
                        "& fieldset": {
                          borderColor: theme.palette.secondary.main,
                        },
                      },
                    }}
                  />
                  <TextField
                    label="Max Length"
                    type="number"
                    value={maxLength}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || (!isNaN(value) && parseInt(value) >= 0)) {
                        setMaxLength(value);
                      }
                    }}
                    inputProps={{
                      min: minLength ? parseInt(minLength) : 0,
                      sx: {
                        fontSize: 16,
                        fontWeight: 400,
                        color: theme.palette.primary.main,
                      },
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "white",
                        "& fieldset": {
                          borderColor: theme.palette.secondary.main,
                        },
                      },
                    }}
                    helperText={
                      minLength &&
                      maxLength &&
                      parseInt(maxLength) < parseInt(minLength)
                        ? "Max must be >= Min"
                        : ""
                    }
                    error={
                      minLength &&
                      maxLength &&
                      parseInt(maxLength) < parseInt(minLength)
                    }
                  />
                </Stack>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: theme.palette.text.secondary,
                    mt: 1,
                    fontStyle: "italic",
                  }}
                >
                  Leave empty to use default values from the selected rule
                </Typography>
              </Box>
            </>
          )}

          {selectedRule && selectedRule !== "custom" && (
            <>
              <Divider />
              <Box
                sx={{
                  pt: 2,
                  pl: 3,
                  pr: 3,
                  pb: 3,
                  borderRadius: 3,
                  bgcolor: theme.palette.popupBlue.main,
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    mb: 1.5,
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  }}
                >
                  Final Regex Pattern
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "white",
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.secondary.main}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontSize: 14,
                      color: theme.palette.primary.main,
                      wordBreak: "break-all",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {getFinalRegex() || "No validation rule selected"}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center" }}>
        <Stack direction={"row"} sx={{ gap: 2, pb: 2, width: 450 }}>
          <Button
            onClick={onClose}
            variant="contained"
            color="lightTheme"
            sx={{
              width: "100%",
              height: 50,
              fontSize: 16,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
            }}
            disableElevation
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            color="theme"
            sx={{
              width: "100%",
              height: 50,
              fontSize: 16,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
            }}
            disabled={!formField.label.trim() || isLoading}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

AddPageFieldModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  formID: PropTypes.string,
  field: PropTypes.object,
  isEdit: PropTypes.bool,
};
